import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../utils/api";
import { showToast } from "../../helper/generalHelper";
import { toast } from "react-toastify";
import { FiArrowLeft, FiTrash2, FiEye, FiEyeOff } from "react-icons/fi";

const EditStaff = () => {
  const navigate = useNavigate();
  const { staffId } = useParams();
  const isStaffRoute = window.location.pathname.includes("/restaurant-staff");
  const routePrefix = isStaffRoute ? "/restaurant-staff/employee" : "/restaurant";
  const apiPrefix = isStaffRoute ? "restaurant-staff" : "restaurant";

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    password: "",
    restaurantId: "",
    roleId: "",
    profileImage: null,
  });

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [data, setData] = useState({
    restaurants: [],
    roles: [],
  });

  useEffect(() => {
    if (staffId) {
      Promise.all([
        fetchStaffData(staffId),
        fetchRestaurantsAndRoles(),
      ]).then(() => setInitialLoading(false));
    }
  }, [staffId]);

  const fetchStaffData = async (id) => {
    try {
      setError("");
      const response = await api.get(`${apiPrefix}/staff/all`, { params: { id } });

      if (!response || !response.success) {
        setError(response?.message || "Staff member not found");
        return;
      }

      // Backend returns a single staff object when fetching by ID
      // response.data could be: a single staff object, an array, or nested
      let staff = null;
      if (response.data && !Array.isArray(response.data) && response.data.id) {
        // Single staff object returned directly
        staff = response.data;
      } else if (Array.isArray(response.data) && response.data.length > 0) {
        staff = response.data[0];
      } else if (response.data?.data) {
        // Nested data
        const nested = response.data.data;
        if (Array.isArray(nested) && nested.length > 0) {
          staff = nested[0];
        } else if (nested && nested.id) {
          staff = nested;
        }
      }

      if (staff) {
        setFormData({
          firstName: staff.first_name || "",
          lastName: staff.last_name || "",
          mobile: staff.mobile || "",
          email: staff.email || "",
          password: "", // Don't load password for security
          restaurantId: staff.restaurant_id || "",
          roleId: staff.role_id || "",
          profileImage: null,
        });

        // Set preview if profile image exists
        if (staff.profile_image_path || staff.profile_image) {
          setPreview(staff.profile_image_path || staff.profile_image);
        }
      } else {
        setError("Staff member not found");
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      setError("Failed to load staff information");
    }
  };

  const fetchRestaurantsAndRoles = async () => {
    try {
      setError("");
      const restaurantEndpoint = isStaffRoute ? `${apiPrefix}/restaurant/get` : `${apiPrefix}/restaurant/all`;
      const [restaurantRes, rolesRes] = await Promise.all([
        api.get(restaurantEndpoint),
        api.get(`${apiPrefix}/staff/roles`),
      ]);

      let restaurants = [];
      if (isStaffRoute) {
        // Staff route returns a single restaurant object directly in data
        if (restaurantRes.data && restaurantRes.data.id) {
          restaurants = [restaurantRes.data];
        }
      } else {
        // Admin route returns an array
        restaurants = restaurantRes.data?.reataurants || [];
      }

      const rolesData = rolesRes.data?.data || rolesRes.data;

      setData({
        restaurants: restaurants,
        roles: Array.isArray(rolesData.roles) ? rolesData.roles : [],
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load restaurants and roles");
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profileImage") {
      const file = files[0];
      setFormData({ ...formData, profileImage: file });
      setPreview(URL.createObjectURL(file));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName.trim()) {
      setError("First name is required");
      return;
    }
    if (!formData.mobile.trim()) {
      setError("Mobile number is required");
      return;
    }
    if (!formData.restaurantId) {
      setError("Please select a business/branch");
      return;
    }
    if (!formData.roleId) {
      setError("Please select a role");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Build multipart/form-data
      const payload = new FormData();
      payload.append("staffId", staffId);
      for (const key in formData) {
        if (key !== "profileImage" && formData[key]) {
          payload.append(key, formData[key]);
        }
      }
      if (formData.profileImage) {
        payload.append("profileImage", formData.profileImage);
      }

      const response = await api.post(`${apiPrefix}/staff/update`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.success) {
        setSuccess("Staff member updated successfully!");
        toast.success("Staff member updated successfully!");

        // Redirect after 2 seconds
        setTimeout(() => {
          navigate(`${routePrefix}/staff`);
        }, 2000);
      }
    } catch (error) {
      console.error("Error updating staff:", error);
      const errorMsg =
        error.response?.data?.message || error.message || "Failed to update staff";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await api.post(`${apiPrefix}/staff/delete`, {
        staffId: staffId,
      });

      if (response.success) {
        toast.success("Staff member deleted successfully!");
        setTimeout(() => {
          navigate(`${routePrefix}/staff`);
        }, 1000);
      }
    } catch (error) {
      console.error("Error deleting staff:", error);
      const errorMsg =
        error.response?.data?.message || error.message || "Failed to delete staff";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading staff information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <button
              className="text-2xl text-gray-600 hover:text-gray-800 mr-4"
              onClick={() => navigate(-1)}
            >
              <FiArrowLeft />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">Edit Staff Member</h1>
          </div>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-red-600 hover:text-red-800 text-2xl transition-colors"
            title="Delete staff"
          >
            <FiTrash2 />
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Confirm Delete</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this staff member? This action cannot be undone.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex-1 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {loading ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700 text-sm">{success}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6" autoComplete="off">
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                placeholder="Enter first name"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="off"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                placeholder="Enter last name"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <input
                type="tel"
                name="mobile"
                placeholder="Enter mobile number"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.mobile}
                onChange={handleChange}
                autoComplete="off"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                placeholder="Enter email (optional)"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.email}
                onChange={handleChange}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Change Password (leave blank to keep current password)
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                placeholder="Enter new password if you want to change it"
                className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Leave empty to keep the current password
            </p>
          </div>

          {/* Business/Branch Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign to Business/Branch *
            </label>
            <select
              name="restaurantId"
              className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.restaurantId}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Business/Branch --</option>
              {data.restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.business_name} {restaurant.parent_id ? "(Branch)" : "(Business)"}
                </option>
              ))}
            </select>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assign Role *
            </label>
            <select
              name="roleId"
              className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.roleId}
              onChange={handleChange}
              required
            >
              <option value="">-- Select Role --</option>
              {data.roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.role_name || role.name}
                </option>
              ))}
            </select>
          </div>

          {/* Profile Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Profile Image
            </label>
            <input
              type="file"
              name="profileImage"
              accept="image/*"
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {preview && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Current/Preview:</p>
                <img
                  src={preview}
                  alt="Preview"
                  className="w-32 h-32 rounded-lg object-cover border border-gray-200"
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate(`${routePrefix}/staff`)}
              className="flex-1 py-3 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {loading ? "Updating..." : "Update Staff Member"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStaff;
